import { z } from "zod";

/**
 * The parts of the public website the admin can edit.
 *
 * Every field is optional, and anything absent falls back to the value
 * committed in apps/site/src/config.ts. That is the whole design: the repo
 * stays the default, the published document holds only what has actually been
 * changed, and clearing a field returns it to the default instead of blanking
 * the site.
 *
 * Nothing here has a minimum of zero characters. An empty string would be
 * stored as an override and would publish a page with a missing headline or no
 * phone number, which is worse than the placeholder it replaced.
 */

/**
 * One service, matched to the committed list by slug.
 *
 * The slug is a key, not content: it is in the URL as /services#commercial and
 * an editable one would break every link pointing at it. Titles and copy are
 * editable; which services exist is not.
 */
export const siteService = z.object({
  slug: z.string().min(1).max(40),
  title: z.string().min(1).max(80),
  summary: z.string().min(1).max(400),
  points: z.array(z.string().min(1).max(120)).max(8),
});
export type SiteService = z.infer<typeof siteService>;

export const siteContent = z.object({
  tagline: z.string().min(1).max(120).optional(),
  phone: z.string().min(7).max(30).optional(),
  email: z.email().max(254).optional(),
  hours: z.string().min(1).max(200).optional(),
  foundedYear: z.number().int().min(1900).max(2100).optional(),

  /**
   * Towns served. These are not decoration: each one becomes an areaServed
   * entry in the structured data Google reads, so a town listed here is a town
   * being claimed.
   */
  serviceAreas: z.array(z.string().min(1).max(60)).max(30).optional(),

  /**
   * Insurance, bonding, and similar claims, shown prominently on the homepage.
   * An empty list renders nothing and breaks no layout, which is the correct
   * state for a claim that cannot be substantiated.
   */
  credentials: z.array(z.string().min(1).max(120)).max(10).optional(),

  services: z.array(siteService).max(10).optional(),

  /**
   * Look and feel. Slugs rather than colours: the palettes have been checked
   * for contrast, and a free colour picker on a business site reliably produces
   * white text on something you cannot read it against.
   *
   * Validated loosely as short strings so adding a palette to the catalogue is
   * a one-line change here-free; an unknown slug falls back to the default
   * rather than failing a save or breaking a build.
   */
  theme: z
    .object({
      palette: z.string().max(40).optional(),
      shape: z.string().max(40).optional(),
      logo: z.string().max(40).optional(),
    })
    .optional(),
});
export type SiteContent = z.infer<typeof siteContent>;

/**
 * Which fields the site treats as unverified until someone confirms them.
 *
 * These carry TODO markers in the committed config because they were guesses.
 * The admin uses this to say so, rather than presenting a placeholder as
 * though it were checked.
 */
export const unverifiedFields = ["hours", "credentials", "foundedYear"] as const;
